import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { getAllStudents } from '@/lib/db'
import type { Student } from '@/types'
import {
  TrendingUp,
  Loader2,
  Search,
  Filter,
  Users,
  CheckCircle,
  PenTool,
  Video,
  GraduationCap,
  Calendar,
  ChevronRight,
  Eye,
  Layers,
  X,
  Columns3
} from 'lucide-react'

interface CohortLite {
  id: string
  name: string
  student_ids: string[]
  level_id: string | null
}

interface UnitProgressRow { student_id: string; unit_id: string; status: string; lessons_completed: number; total_lessons: number; exam_passed: boolean | null; updated_at: string | null }
interface LessonProgressRow { student_id: string; lesson_id: string; status: string; contents_completed: number; total_contents: number; updated_at: string | null }
interface ContentProgressRow { student_id: string; content_id: string; status: string; total_exercises_completed: number; total_exercises: number; completed_at: string | null; updated_at: string | null }
interface AttendanceRow { student_id: string; status: string; marked_at: string | null }
interface ExamRow { student_id: string; passed: boolean | null; completed_at: string | null }

interface StudentRow {
  student: Student
  cohorts: CohortLite[]
  unitsCompleted: number
  unitsTotal: number
  lessonsCompleted: number
  contentsCompleted: number
  exercisesCorrect: number
  workshopsAttended: number
  examsPassed: number
  examsAttempted: number
  lastActivity: string | null
}

// ============================================
// Configuración de columnas
// ============================================
type ColTarget = 'profile' | 'student' | 'computed'
type ColType = 'text' | 'email' | 'date' | 'select' | 'metric' | 'static'

interface ColumnDef {
  id: string
  label: string
  defaultVisible: boolean
  target: ColTarget
  field?: string
  type: ColType
  options?: { value: string; label: string }[]
  align?: 'left' | 'right'
}

const COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Alumno', defaultVisible: true, target: 'profile', field: 'first_name', type: 'text', align: 'left' },
  { id: 'email', label: 'Email', defaultVisible: false, target: 'profile', field: 'email', type: 'email', align: 'left' },
  { id: 'phone', label: 'Teléfono', defaultVisible: false, target: 'profile', field: 'phone', type: 'text', align: 'left' },
  { id: 'cohorts', label: 'Cohorte', defaultVisible: true, target: 'computed', type: 'static', align: 'left' },
  { id: 'subscription_status', label: 'Suscripción', defaultVisible: true, target: 'student', field: 'subscription_status', type: 'select', align: 'left',
    options: [
      { value: 'active', label: 'active' },
      { value: 'trial', label: 'trial' },
      { value: 'expired', label: 'expired' },
      { value: 'cancelled', label: 'cancelled' }
    ] },
  { id: 'enrollment_date', label: 'Inscripción', defaultVisible: false, target: 'student', field: 'enrollment_date', type: 'date', align: 'left' },
  { id: 'birth_date', label: 'Nacimiento', defaultVisible: false, target: 'student', field: 'birth_date', type: 'date', align: 'left' },
  { id: 'lessons', label: 'Lec.', defaultVisible: true, target: 'computed', type: 'metric', align: 'right' },
  { id: 'contents', label: 'Cont.', defaultVisible: true, target: 'computed', type: 'metric', align: 'right' },
  { id: 'exercises', label: 'Ej. ✓', defaultVisible: true, target: 'computed', type: 'metric', align: 'right' },
  { id: 'workshops', label: 'Tall.', defaultVisible: true, target: 'computed', type: 'metric', align: 'right' },
  { id: 'exams', label: 'Exám.', defaultVisible: true, target: 'computed', type: 'metric', align: 'right' },
  { id: 'last_activity', label: 'Última actividad', defaultVisible: true, target: 'computed', type: 'static', align: 'left' }
]

const STORAGE_KEY = 'admin_progress_visible_cols'

// ============================================
// Helpers
// ============================================
function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
}

function maxDate(...dates: (string | null | undefined)[]): string | null {
  let best: number | null = null
  for (const d of dates) {
    if (!d) continue
    const t = new Date(d).getTime()
    if (Number.isNaN(t)) continue
    if (best === null || t > best) best = t
  }
  return best ? new Date(best).toISOString() : null
}

// Convierte ISO/timestamp a "YYYY-MM-DD" para input type="date"
function toDateInputValue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// ============================================
// Componente de celda editable inline
// ============================================
function EditableCell({
  value, type, options, onSave
}: {
  value: string | null
  type: ColType
  options?: { value: string; label: string }[]
  onSave: (newValue: string | null) => Promise<{ error: Error | null }>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)

  useEffect(() => { setDraft(value || '') }, [value])
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      if ('select' in inputRef.current) inputRef.current.select()
    }
  }, [editing])

  const commit = async (newRaw: string) => {
    const next = newRaw.trim() === '' ? null : newRaw
    if (next === value) { setEditing(false); return }
    setSaving(true)
    const { error } = await onSave(next)
    setSaving(false)
    if (error) {
      alert(`No se pudo guardar: ${error.message}`)
      setDraft(value || '')
      return
    }
    setEditing(false)
  }

  if (saving) {
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500 inline-block" />
  }

  if (editing) {
    if (type === 'select') {
      return (
        <select
          ref={inputRef as any}
          value={draft}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
          className="px-2 py-1 border border-emerald-400 rounded bg-white text-xs"
        >
          {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
    }
    return (
      <input
        ref={inputRef as any}
        type={type === 'date' ? 'date' : type === 'email' ? 'email' : 'text'}
        value={type === 'date' ? toDateInputValue(draft) : draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(draft)
          else if (e.key === 'Escape') { setDraft(value || ''); setEditing(false) }
        }}
        onBlur={() => commit(draft)}
        className="px-2 py-1 border border-emerald-400 rounded bg-white w-full text-xs"
      />
    )
  }

  let display: string
  if (type === 'date') display = value ? formatDate(value) : '—'
  else display = value || '—'

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-left w-full px-1 py-0.5 rounded hover:bg-amber-50 hover:outline hover:outline-1 hover:outline-dashed hover:outline-amber-300 cursor-text"
      title="Click para editar"
    >
      {display}
    </button>
  )
}

// ============================================
// Página principal
// ============================================
export default function ProgressDashboard() {
  const [rows, setRows] = useState<StudentRow[]>([])
  const [cohortsAll, setCohortsAll] = useState<CohortLite[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [cohortFilter, setCohortFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [activityFilter, setActivityFilter] = useState<'all' | '7d' | '30d' | 'inactive30' | 'never'>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [visibleCols, setVisibleCols] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) return new Set(JSON.parse(raw))
    } catch { /* ignore */ }
    return new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.id))
  })
  const [colMenuOpen, setColMenuOpen] = useState(false)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleCols))) } catch { /* ignore */ }
  }, [visibleCols])

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [studentsRes, cohortsRes, unitsRes, lessonsRes, contentsRes, examRes, attendanceRes, exerciseRes] = await Promise.all([
        getAllStudents(),
        supabase.from('cohorts').select('id, name, student_ids, level_id').eq('is_active', true),
        supabase.from('student_unit_progress').select('student_id, unit_id, status, lessons_completed, total_lessons, exam_passed, updated_at'),
        supabase.from('student_lesson_progress').select('student_id, lesson_id, status, contents_completed, total_contents, updated_at'),
        supabase.from('student_content_progress').select('student_id, content_id, status, total_exercises_completed, total_exercises, completed_at, updated_at'),
        (supabase.from('exam_attempts') as any).select('student_id, passed, completed_at'),
        (supabase.from('workshop_attendance') as any).select('student_id, status, marked_at'),
        (supabase.from('exercise_results') as any).select('student_id, is_correct')
      ])

      if (studentsRes.error) throw studentsRes.error

      const students = (studentsRes.data || []) as Student[]
      const cohorts = ((cohortsRes.data as CohortLite[]) || [])
      setCohortsAll(cohorts)

      const groupBy = <T extends { student_id: string }>(arr: T[]) => {
        const m = new Map<string, T[]>()
        for (const r of arr) {
          const list = m.get(r.student_id) || []
          list.push(r)
          m.set(r.student_id, list)
        }
        return m
      }

      const unitsByStudent = groupBy((unitsRes.data as UnitProgressRow[]) || [])
      const lessonsByStudent = groupBy((lessonsRes.data as LessonProgressRow[]) || [])
      const contentsByStudent = groupBy((contentsRes.data as ContentProgressRow[]) || [])
      const examsByStudent = groupBy((examRes.data as ExamRow[]) || [])
      const attendanceByStudent = groupBy((attendanceRes.data as AttendanceRow[]) || [])

      const correctByStudent = new Map<string, number>()
      const exerciseRows = (exerciseRes.data as { student_id: string; is_correct: boolean | null }[]) || []
      for (const er of exerciseRows) {
        if (er.is_correct === true) {
          correctByStudent.set(er.student_id, (correctByStudent.get(er.student_id) || 0) + 1)
        }
      }

      const studentRows: StudentRow[] = students.map(s => {
        const studentCohorts = cohorts.filter(c => (c.student_ids || []).includes(s.profile_id))
        const ups = unitsByStudent.get(s.id) || []
        const lps = lessonsByStudent.get(s.id) || []
        const cps = contentsByStudent.get(s.id) || []
        const exams = examsByStudent.get(s.id) || []
        const attend = attendanceByStudent.get(s.id) || []
        return {
          student: s,
          cohorts: studentCohorts,
          unitsCompleted: ups.filter(u => u.status === 'completed').length,
          unitsTotal: ups.length,
          lessonsCompleted: lps.filter(l => l.status === 'completed').length,
          contentsCompleted: cps.filter(c => c.status === 'completed').length,
          exercisesCorrect: correctByStudent.get(s.id) || 0,
          workshopsAttended: attend.filter(a => a.status === 'attended').length,
          examsPassed: exams.filter(e => e.passed === true).length,
          examsAttempted: exams.length,
          lastActivity: maxDate(
            s.last_activity_date,
            ...ups.map(u => u.updated_at),
            ...lps.map(l => l.updated_at),
            ...cps.map(c => c.updated_at || c.completed_at),
            ...exams.map(e => e.completed_at),
            ...attend.map(a => a.marked_at)
          )
        }
      })

      setRows(studentRows)
    } catch (e: any) {
      setError(e?.message || 'Error al cargar datos')
    } finally {
      setIsLoading(false)
    }
  }

  // ============================================
  // Save inline editing
  // ============================================
  const saveProfileField = (profileId: string, field: string) => async (newValue: string | null) => {
    const { error } = await (supabase.from('profiles') as any).update({ [field]: newValue }).eq('id', profileId)
    if (error) return { error: new Error(error.message) }
    setRows(prev => prev.map(r =>
      r.student.profile_id === profileId
        ? { ...r, student: { ...r.student, profile: { ...(r.student as any).profile, [field]: newValue } } as any }
        : r
    ))
    return { error: null }
  }

  const saveStudentField = (studentId: string, field: string) => async (newValue: any) => {
    const { error } = await (supabase.from('students') as any).update({ [field]: newValue }).eq('id', studentId)
    if (error) return { error: new Error(error.message) }
    setRows(prev => prev.map(r =>
      r.student.id === studentId
        ? { ...r, student: { ...r.student, [field]: newValue } as Student }
        : r
    ))
    return { error: null }
  }

  // ============================================
  // Filtros + selección + métricas
  // ============================================
  const filtered = useMemo(() => {
    const now = Date.now()
    const day = 24 * 60 * 60 * 1000
    return rows.filter(r => {
      const profile = (r.student as any).profile
      const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
      if (search) {
        const term = search.toLowerCase()
        const haystack = [fullName, profile?.email, profile?.phone].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(term)) return false
      }
      if (cohortFilter !== 'all') {
        if (cohortFilter === 'none') {
          if (r.cohorts.length > 0) return false
        } else if (!r.cohorts.some(c => c.id === cohortFilter)) {
          return false
        }
      }
      if (statusFilter !== 'all' && r.student.subscription_status !== statusFilter) return false
      if (activityFilter !== 'all') {
        const last = r.lastActivity ? new Date(r.lastActivity).getTime() : null
        if (activityFilter === 'never' && last !== null) return false
        if (activityFilter === '7d' && (!last || now - last > 7 * day)) return false
        if (activityFilter === '30d' && (!last || now - last > 30 * day)) return false
        if (activityFilter === 'inactive30' && last && now - last <= 30 * day) return false
      }
      return true
    })
  }, [rows, search, cohortFilter, statusFilter, activityFilter])

  const totalSelected = selected.size
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.student.id))

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => { const next = new Set(prev); for (const r of filtered) next.delete(r.student.id); return next })
    } else {
      setSelected(prev => { const next = new Set(prev); for (const r of filtered) next.add(r.student.id); return next })
    }
  }
  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next
  })

  const stats = useMemo(() => {
    const base = totalSelected > 0 ? rows.filter(r => selected.has(r.student.id)) : filtered
    if (base.length === 0) return null
    const sum = (fn: (r: StudentRow) => number) => base.reduce((a, r) => a + fn(r), 0)
    return {
      n: base.length,
      lessonsCompleted: sum(r => r.lessonsCompleted),
      contentsCompleted: sum(r => r.contentsCompleted),
      exercisesCorrect: sum(r => r.exercisesCorrect),
      workshopsAttended: sum(r => r.workshopsAttended),
      examsPassed: sum(r => r.examsPassed),
      examsAttempted: sum(r => r.examsAttempted)
    }
  }, [rows, filtered, selected, totalSelected])

  const toggleColumn = (id: string) => setVisibleCols(prev => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next
  })
  const visibleColumns = COLUMNS.filter(c => visibleCols.has(c.id))

  // ============================================
  // Render de cada celda
  // ============================================
  const renderCell = (col: ColumnDef, r: StudentRow) => {
    const profile = (r.student as any).profile

    if (col.id === 'name') {
      // Editamos first_name y last_name por separado, mostrados juntos
      return (
        <div className="flex flex-wrap gap-1 items-center">
          <EditableCell
            value={profile?.first_name || ''}
            type="text"
            onSave={saveProfileField(r.student.profile_id, 'first_name')}
          />
          <EditableCell
            value={profile?.last_name || ''}
            type="text"
            onSave={saveProfileField(r.student.profile_id, 'last_name')}
          />
        </div>
      )
    }

    if (col.id === 'cohorts') {
      return r.cohorts.length === 0
        ? <span className="text-xs text-gray-400">Sin cohorte</span>
        : (
          <div className="flex flex-wrap gap-1">
            {r.cohorts.map(c => (
              <span key={c.id} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">{c.name}</span>
            ))}
          </div>
        )
    }

    if (col.id === 'lessons') return <span className="tabular-nums">{r.lessonsCompleted}</span>
    if (col.id === 'contents') return <span className="tabular-nums">{r.contentsCompleted}</span>
    if (col.id === 'exercises') return <span className="tabular-nums">{r.exercisesCorrect}</span>
    if (col.id === 'workshops') return <span className="tabular-nums">{r.workshopsAttended}</span>
    if (col.id === 'exams') return <span className="tabular-nums">{r.examsPassed}/{r.examsAttempted}</span>
    if (col.id === 'last_activity') return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
        <Calendar className="w-3 h-3" />
        {formatDate(r.lastActivity)}
      </span>
    )

    // Resto: editable según target/type
    if (col.target === 'profile' && col.field) {
      const current = (profile?.[col.field] ?? '') as string
      return (
        <EditableCell
          value={current}
          type={col.type}
          options={col.options}
          onSave={saveProfileField(r.student.profile_id, col.field)}
        />
      )
    }
    if (col.target === 'student' && col.field) {
      const current = ((r.student as any)[col.field] ?? '') as string
      return (
        <EditableCell
          value={current}
          type={col.type}
          options={col.options}
          onSave={saveStudentField(r.student.id, col.field)}
        />
      )
    }
    return null
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-emerald-500" />
          Avance de alumnos
        </h1>
        <p className="text-gray-600 mt-1">
          Filtrá, seleccioná, y editá datos directo desde la tabla. Click en la celda → cambiá → Tab/Enter para guardar.
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono…"
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          <select value={cohortFilter} onChange={e => setCohortFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="all">Todas las cohortes</option>
            <option value="none">Sin cohorte</option>
            {cohortsAll.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="all">Toda suscripción</option>
            <option value="active">Activa</option>
            <option value="trial">Trial</option>
            <option value="expired">Expirada</option>
            <option value="cancelled">Cancelada</option>
          </select>
          <select value={activityFilter} onChange={e => setActivityFilter(e.target.value as any)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="all">Toda actividad</option>
            <option value="7d">Activo últimos 7 días</option>
            <option value="30d">Activo últimos 30 días</option>
            <option value="inactive30">Inactivo +30 días</option>
            <option value="never">Sin actividad</option>
          </select>

          {/* Selector de columnas */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setColMenuOpen(!colMenuOpen)}
              className="px-3 py-2 border rounded-lg text-sm inline-flex items-center gap-1 hover:bg-gray-50"
            >
              <Columns3 className="w-4 h-4" />
              Columnas ({visibleCols.size})
            </button>
            {colMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setColMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-40 p-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">Columnas visibles</p>
                  {COLUMNS.map(col => (
                    <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={visibleCols.has(col.id)}
                        onChange={() => toggleColumn(col.id)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Métricas agregadas */}
      {stats && (
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">
              {totalSelected > 0
                ? `Métricas de los ${stats.n} alumnos seleccionados`
                : `Métricas de los ${stats.n} alumnos filtrados`}
            </p>
            {totalSelected > 0 && (
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                <X className="w-3 h-3" />Limpiar selección
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon={Layers} label="Lecciones" value={stats.lessonsCompleted} color="text-blue-600 bg-blue-50" />
            <Stat icon={CheckCircle} label="Contenidos" value={stats.contentsCompleted} color="text-purple-600 bg-purple-50" />
            <Stat icon={PenTool} label="Ej. correctos" value={stats.exercisesCorrect} color="text-amber-600 bg-amber-50" />
            <Stat icon={Video} label="Talleres asist." value={stats.workshopsAttended} color="text-pink-600 bg-pink-50" />
            <Stat icon={GraduationCap} label="Exám. aprob." value={`${stats.examsPassed}/${stats.examsAttempted}`} color="text-green-600 bg-green-50" />
            <Stat icon={Users} label="Alumnos" value={stats.n} color="text-gray-600 bg-gray-100" />
          </div>
        </div>
      )}

      {/* Tabla */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay alumnos con esos filtros.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} />
                  </th>
                  {visibleColumns.map(col => (
                    <th key={col.id} className={`px-3 py-2 font-medium text-gray-600 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                      {col.label}
                    </th>
                  ))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(r => {
                  const isSelected = selected.has(r.student.id)
                  return (
                    <tr key={r.student.id} className={isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-2 align-top">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleOne(r.student.id)} />
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.id} className={`px-3 py-2 align-top ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                          {renderCell(col, r)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right align-top">
                        <Link
                          to={`/admin/progress/${r.student.profile_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                        >
                          <Eye className="w-3.5 h-3.5" />Ver<ChevronRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 text-center py-3 border-t">
            Mostrando {filtered.length} de {rows.length} alumnos. {visibleCols.size}/{COLUMNS.length} columnas.
          </p>
        </div>
      )}
    </div>
  )
}

function Stat({ icon: Icon, label, value, color }: { icon: any; label: string; value: number | string; color: string }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold text-gray-900 leading-tight tabular-nums">{value}</p>
        <p className="text-xs text-gray-500 leading-tight truncate">{label}</p>
      </div>
    </div>
  )
}
